'use client';

import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import type { FormData } from '../FormProject';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

type Props = {
  formData: FormData;
  updateFormData: (data: Partial<FormData>) => void;
};

export default function Step3Inspirations({ formData, updateFormData }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imagePreviews, setImagePreviews] = useState<Array<string>>([]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const validFiles = Array.from(e.target.files || []).filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} dépasse la limite de 5MB`);
        return false;
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name} n'est pas un format d'image autorisé`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    const newImages = [...formData.inspirationImages, ...validFiles];
    updateFormData({ inspirationImages: newImages });

    // Create previews
    const newPreviews = validFiles.map((file) => URL.createObjectURL(file));
    setImagePreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeImage = (index: number) => {
    const newImages = formData.inspirationImages.filter((_, i) => i !== index);
    updateFormData({ inspirationImages: newImages });

    // Revoke URL and update previews
    URL.revokeObjectURL(imagePreviews[index]);
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Vos inspirations</h2>
        <p className="mt-2 text-gray-600">
          Partagez des images, références ou idées qui vous inspirent
        </p>
      </div>

      {/* Inspiration text */}
      <div>
        <label htmlFor="inspirationText" className="block text-sm font-medium text-gray-700">
          Décrivez vos inspirations
        </label>
        <textarea
          id="inspirationText"
          rows={4}
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 focus:outline-none"
          placeholder="Parlez-nous de ce qui vous inspire : artistes, œuvres, symboles, couleurs..."
          value={formData.inspirationText}
          onChange={(e) => updateFormData({ inspirationText: e.target.value })}
        />
      </div>

      {/* Image upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Images d'inspiration</label>
        <p className="mt-1 text-sm text-gray-500">
          Téléchargez des images de référence (PNG, JPG, max 5MB chacune)
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="mt-2 flex w-full items-center justify-center rounded-lg border-2 border-dashed border-gray-300 px-6 py-8 transition-colors hover:border-emerald-400 hover:bg-emerald-50"
        >
          <div className="text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-600">
              <span className="font-semibold text-emerald-600">Cliquez pour télécharger</span> ou
              glissez-déposez
            </p>
          </div>
        </button>

        {/* Image previews */}
        {imagePreviews.length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
            {imagePreviews.map((preview, index) => (
              <div key={index} className="group relative">
                <img
                  src={preview}
                  alt={`Inspiration ${index + 1}`}
                  className="h-32 w-full rounded-lg object-cover"
                  width={384}
                  height={128}
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-2 right-2 rounded-full bg-red-500 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* URL references */}
      <div>
        <label htmlFor="inspirationUrls" className="block text-sm font-medium text-gray-700">
          Liens vers vos inspirations
        </label>
        <textarea
          id="inspirationUrls"
          rows={3}
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 focus:outline-none"
          placeholder="Collez ici des liens Pinterest, Instagram ou autres (un par ligne)"
          value={formData.inspirationUrls}
          onChange={(e) => updateFormData({ inspirationUrls: e.target.value })}
        />
      </div>

      {/* Info box */}
      <div className="rounded-lg bg-emerald-50 p-6">
        <h3 className="font-semibold text-emerald-900">💡 Astuce</h3>
        <p className="mt-2 text-sm text-emerald-800">
          Plus vous partagez d'inspirations, mieux nous pourrons comprendre votre vision et créer un
          design unique qui vous correspond.
        </p>
      </div>
    </div>
  );
}
